import app from 'flarum/forum/app';
import PageNav from './PageNav';
import Page from 'flarum/common/components/Page';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
// Flarum 2 renders icons through a component. The legacy
// `common/helpers/icon` module is still in the registry but its default export
// is not a function, so calling it throws and takes the whole page with it.
import Icon from 'flarum/common/components/Icon';
import extractText from 'flarum/common/utils/extractText';
import m from 'mithril';
import { featured } from '../reviews';
const T = function (key, p) {
  if (p === void 0) {
    p = {};
  }
  return app.translator.trans(`linkrobins-lr-home.forum.${key}`, p);
};

/**
 * The uptime section, handed over by lr-monitoring's initializer rather than
 * imported from it.
 *
 * Deliberately NOT `import ... from 'ext:linkrobins/lr-monitoring/...'`. An
 * `ext:` import is resolved when this bundle is evaluated, so it yields
 * undefined whenever lr-monitoring is disabled or loads second, and a page that
 * renders an undefined component renders EMPTY — the whole home page, not just
 * the section. That is what had to be reverted on 2026-09-27. Read at render
 * time instead: if it is not there, the section is not there, and the rest of
 * the page is unaffected. See lr-monitoring's forum.tsx.
 */
function uptimeLanding() {
  return app.lrUptimeLanding;
}
function uptimeSection() {
  const Section = uptimeLanding();
  return Section ? m(Section, null) : null;
}

/**
 * The landing page, served at / via the default_route setting.
 *
 * Uptime monitoring for Flarum is the whole of it (Karl, 2026-10-06): the
 * hero, the live monitor and what it checks (lr-monitoring's section), how it
 * works, Free vs Pro, the reviews, one closing ask. Consulting, the extension
 * strip and extension requests were removed the same day; the extensions live
 * on as threads on the forum and are not mentioned here.
 *
 * Everything is styled from Flarum's own custom properties rather than fixed
 * colors, so it follows the forum's theme (and its dark mode).
 */
export default class HomePage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.bodyClass = 'App--lr-home';
    // At / the home title pattern applies (locale-overrides); elsewhere this
    // reads "Uptime monitoring for Flarum - Link Robins".
    app.setTitle(extractText(T('meta.page_title')));
    app.setTitleCount(0);
  }

  /** Sign up (guests) or open the dashboard (members). */
  start() {
    if (app.session.user) {
      m.route.set(app.route('lr.monitors'));
      return;
    }
    app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal'));
  }
  view() {
    const signedIn = !!app.session.user;
    return m("div", {
      className: "LrHome"
    }, m(PageNav, {
      page: "home"
    }), m("section", {
      className: "LrHome-hero"
    }, m("div", {
      className: "container"
    }, m("span", {
      className: "LrHome-pill"
    }, T('hero.eyebrow')), m("h1", {
      className: "LrHome-title"
    }, T('hero.title'), " ", m("span", {
      className: "LrHome-titleAccent"
    }, T('hero.title_accent'))), m("p", {
      className: "LrHome-lede"
    }, T('hero.lede')), m("div", {
      className: "LrHome-heroActions"
    }, m(Button, {
      className: "Button Button--primary LrHome-cta",
      onclick: () => this.start()
    }, signedIn ? T('hero.cta_signed_in') : T('hero.cta')), m("a", {
      className: "Button LrHome-cta LrHome-cta--outline",
      href: "#pricing",
      onclick: e => this.toPricing(e)
    }, T('hero.cta_pricing'))), m("ul", {
      className: "LrHome-trust"
    }, m("li", null, m(Icon, {
      name: "fas fa-user-group"
    }), " ", T('trust.one')), m("li", null, m(Icon, {
      name: "fas fa-circle-check"
    }), " ", T('trust.two'))))), uptimeSection(), this.how(), this.prices(), this.loveBand(), m("section", {
      className: "LrHome-final"
    }, m("div", {
      className: "container"
    }, m("h2", {
      className: "LrHome-finalTitle"
    }, T('final.title'), " ", m("span", {
      className: "LrHome-titleAccent"
    }, T('final.title_accent'))), m("div", {
      className: "LrHome-finalActions"
    }, signedIn ? m(Link, {
      className: "Button Button--primary LrHome-cta",
      href: app.route('lr.monitors')
    }, T('final.dashboard')) : [
    // Core's auth modals are lazy chunks in Flarum 2 — they are
    // opened through a dynamic import, never by class reference.
    m(Button, {
      className: "Button Button--primary LrHome-cta",
      onclick: () => app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal'))
    }, T('final.register')), m(Button, {
      className: "Button LrHome-cta LrHome-cta--outline",
      onclick: () => app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'))
    }, T('final.login'))]))));
  }

  /** Four steps from nothing to watched. */
  how() {
    const steps = [['fas fa-user-plus', 'account'], ['fas fa-link', 'monitor'], ['fas fa-stopwatch', 'check'], ['fas fa-bell', 'alert']];
    return m("section", {
      className: "LrHome-how"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "LrHome-sectionHead"
    }, m("span", {
      className: "LrHome-pill"
    }, T('how.eyebrow')), m("h2", {
      className: "LrHome-sectionTitle"
    }, T('how.title')), m("p", {
      className: "LrHome-sectionLede"
    }, T('how.lede'))), m("ol", {
      className: "LrHome-steps"
    }, steps.map(_ref => {
      let [icon, key] = _ref;
      return m("li", {
        className: "LrHome-step",
        key: key
      }, m("span", {
        className: "LrHome-stepIcon",
        "aria-hidden": "true"
      }, m(Icon, {
        name: icon
      })), m("h3", {
        className: "LrHome-stepTitle"
      }, T(`how.${key}.title`)), m("p", {
        className: "LrHome-stepText"
      }, T(`how.${key}.text`)));
    }))));
  }

  /** Free and Pro, side by side. Pro opens the Pro modal via /dashboard/pro. */
  /**
   * Scroll to the pricing section ourselves rather than leaving it to the
   * browser's jump to #pricing. A hash change also reaches Flarum's router
   * (Mithril listens for popstate), and Karl found the first click often
   * didn't arrive (2026-10-10). Doing it here behaves the same everywhere;
   * the hash is still written, so the address can be shared.
   */
  toPricing(e) {
    const el = document.getElementById('pricing');
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    history.replaceState(history.state, '', '#pricing');
  }
  prices() {
    return m("section", {
      className: "LrHome-prices",
      id: "pricing"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "LrHome-sectionHead"
    }, m("span", {
      className: "LrHome-pill"
    }, T('prices.eyebrow')), m("h2", {
      className: "LrHome-sectionTitle"
    }, T('prices.title')), m("p", {
      className: "LrHome-sectionLede"
    }, T('prices.lede'))), m("div", {
      className: "LrHome-priceCards LrHome-priceCards--two"
    }, this.priceCard({
      key: 'free',
      name: T('prices.free_name'),
      amount: T('prices.free_amount'),
      unit: T('prices.free_unit'),
      sub: T('prices.free_sub'),
      points: [1, 2, 3, 4, 5, 6].map(i => T(`prices.free_point_${i}`)),
      cta: app.session.user ? T('prices.free_cta_signed_in') : T('prices.free_cta'),
      onclick: () => this.start()
    }), this.priceCard({
      key: 'pro',
      // What Pro is for, as a fact rather than "Recommended": the
      // lede above says most forums are well served by Free.
      badge: T('prices.pro_badge'),
      name: T('prices.pro_name'),
      amount: T('prices.pro_amount'),
      unit: T('prices.pro_unit'),
      sub: T('prices.pro_sub'),
      points: [1, 2, 3, 4, 5, 6].map(i => T(`prices.pro_point_${i}`)),
      cta: T('prices.pro_cta'),
      onclick: () => {
        if (app.session.user) {
          m.route.set('/dashboard/pro');
          return;
        }
        app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/SignUpModal'));
      }
    }))));
  }
  priceCard(c) {
    return m("div", {
      className: `LrLander-card LrHome-priceCard LrHome-priceCard--${c.key}`,
      key: c.key
    }, c.badge ? m("span", {
      className: "LrHome-priceBadge"
    }, c.badge) : null, m("h3", {
      className: "LrHome-priceName"
    }, c.name), m("div", {
      className: "LrPrice"
    }, m("div", {
      className: "LrPrice-amount"
    }, m("span", {
      className: "LrPrice-value"
    }, c.amount), m("span", {
      className: "LrPrice-unit"
    }, c.unit)), m("p", {
      className: "LrPrice-sub"
    }, c.sub), m("ul", {
      className: "LrPrice-list"
    }, c.points.map((point, i) => m("li", {
      key: i
    }, m(Icon, {
      name: "fas fa-circle-check"
    }), " ", point))), m(Button, {
      className: "Button Button--primary LrLander-cardCta",
      onclick: c.onclick
    }, c.cta)));
  }
  loveBand() {
    return m("section", {
      className: "LrHome-love"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "LrHome-sectionHead"
    }, m("span", {
      className: "LrHome-pill"
    }, T('love.eyebrow')), m("h2", {
      className: "LrHome-sectionTitle"
    }, T('love.title'))), m("div", {
      className: "LrHome-loveGrid"
    }, featured().map(r => m("figure", {
      className: "LrHome-loveCard",
      key: r.title + r.author
    }, m("div", {
      className: "LrHome-loveStars",
      role: "img",
      "aria-label": extractText(T('love.rating'))
    }, [0, 1, 2, 3, 4].map(i => m(Icon, {
      name: "fas fa-star",
      key: i
    }))), m("figcaption", {
      className: "LrHome-loveTitle"
    }, r.title), m("blockquote", {
      className: "LrHome-loveBody"
    }, r.body), m("div", {
      className: "LrHome-loveAuthor"
    }, m("span", {
      className: "LrHome-loveAvatar",
      "aria-hidden": "true"
    }, r.author.charAt(0)), m("span", null, m("strong", null, r.author), m("span", {
      className: "LrHome-loveWhen"
    }, r.when)))))), m("div", {
      className: "LrHome-loveMore"
    }, m(Link, {
      className: "LrHome-loveAll",
      href: app.route('lr.wall')
    }, T('love.all'), " ", m(Icon, {
      name: "fas fa-arrow-right"
    })))));
  }
}
flarum.reg.add('linkrobins-lr-home', 'forum/components/HomePage', HomePage);